// RUM (Real User Monitoring) — tracks frontend performance and user sessions
// in Datadog: Core Web Vitals, JS errors, route load times, session replay.
import type { EnvironmentProviders, Provider } from '@angular/core';
import { environment } from '../../../environments/environment';

type RumEnv = 'development' | 'staging' | 'preprod' | 'production' | 'review' | 'unknown';

function detectEnv(): RumEnv {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'development';
  if (hostname.includes('-pr') || hostname.includes('review')) return 'review';
  if (hostname === 'staging.app.programme-cee-actee.fr') return 'staging';
  if (hostname === 'preprod.app.programme-cee-actee.fr') return 'preprod';
  if (hostname === 'app.programme-cee-actee.fr') return 'production';
  if (hostname.includes('laureatv2-frontend-staging')) return 'staging';
  if (hostname.includes('laureatv2-frontend-preprod')) return 'preprod';
  if (hostname.includes('laureatv2-frontend-production')) return 'production';
  return 'unknown';
}

export async function initDatadogRum(): Promise<(Provider | EnvironmentProviders)[]> {
  // Turned off in every environment since 2026-10-28 (Datadog monitoring
  // decommissioned). To turn it back on, set rum.enabled to true in
  // environment.prod.ts and environment.staging.ts.
  if (!environment.rum.enabled) return [];

  const env = detectEnv();

  // RUM is only active on preprod — the only environment monitored in Datadog,
  // to keep Datadog costs down. Staging, production, review apps (PRs) and
  // unrecognized hostnames send nothing.
  if (env !== 'preprod') return [];

  const [{ datadogRum }, { angularPlugin, provideDatadogRouter, provideDatadogErrorHandler }] = await Promise.all([
    import('@datadog/browser-rum'),
    import('@datadog/browser-rum-angular'),
  ]);

  datadogRum.init({
    // RUM application credentials — public values, safe to commit
    applicationId: environment.rum.applicationId,
    clientToken: environment.rum.clientToken,
    // Datadog EU datacenter (data hosted in Europe)
    site: 'datadoghq.eu',
    // Identifies this app in Datadog dashboards and traces
    service: 'laureat-v2-frontend',
    // Runtime environment tag (staging / production / review / development)
    env,
    // App version — used to correlate errors with deployments
    version: environment.version,
    // Percentage of user sessions tracked (100 = all sessions)
    sessionSampleRate: 100,
    // No session replay recording (it was production only, and production is
    // not monitored anymore)
    sessionReplaySampleRate: 0,
    // Track clicks, form inputs and other user interactions
    trackUserInteractions: true,
    // Track XHR/fetch requests and static asset loading times
    trackResources: true,
    // Track JS tasks blocking the main thread for more than 50ms
    trackLongTasks: true,
    // Mask form inputs in session replays to protect user data
    defaultPrivacyLevel: 'mask-user-input',
    // Inject Datadog trace headers on API requests to correlate
    // RUM sessions with backend APM traces
    allowedTracingUrls: [{ match: environment.apiUrl, propagatorTypes: ['datadog'] }],
    // Angular plugin: uses route patterns as view names (e.g. /actions/:id)
    // instead of resolved URLs, and reports Angular errors to RUM
    plugins: [angularPlugin({ router: true })],
  });

  return [provideDatadogRouter(), provideDatadogErrorHandler()];
}
